<template>
  <Form id="bivalentVaccineAppointmentFormGroup" @submit="sendMsg" :validation-schema="schema" class="w-3/4 !font-sanstc">
    <div class="flex space-x-14 pt-10">
      <span class="w-full flex items-start">
        <span>{{ t("bivalentVaccine.pleaseSelectOptions") }}</span>
      </span>
    </div>

    <div class="flex space-x-14 pt-10">
      <span class="w-2/3 flex items-center">
        <span>{{ t("bivalentVaccine.option") }}*</span>
        <span class="text-red-500 xl:text-xs xl: font-bold pl-1" id="optionError">{{ optionError }}</span>
      </span>
    </div>
    <div class="flex space-x-14">
      <select name="option" id="option" v-model="option" class="border border-input-border rounded px-3 h-12 w-3/4" style="border-width: 1px">
        <!-- <option value="">{{ t("common.pleaseSelect") }}</option> -->
        <template v-for="(item, index) in bivalentVaccineStore.form.optionList" :key="'option' + index">
          <option :value="item.id">{{ t(item.displayName) }}</option>
        </template>
      </select>
    </div>

    <div class="flex space-x-14 pt-10">
      <span class="w-full flex items-start">
        <span>{{ t("bivalentVaccine.pleaseInputMainContactInfo") }}</span>
      </span>
    </div>

    <div class="flex space-x-14 pt-10">
      <span class="w-1/3 flex items-center"
        ><span>{{ t("bivalentVaccine.name") }}*</span> <span class="text-red-500 xl:text-xs xl: font-bold pl-1" id="nameError">{{ nameError }}</span>
      </span>
      <span class="w-1/3 flex items-center"
        ><span>{{ t("bivalentVaccine.phoneNumber") }}*</span>
        <span class="text-red-500 xl:text-xs xl: font-bold pl-1" id="phoneNumberError">{{ phoneNumberError }}</span>
      </span>
      <span class="w-1/3 flex items-center"
        ><span>{{ t("bivalentVaccine.noOfPeople") }}*</span> <span class="text-red-500 xl:text-xs xl: font-bold pl-1" id="noOfPeopleError">{{ noOfPeopleError }}</span>
      </span>
    </div>
    <div class="flex space-x-14">
      <input v-model="name" type="text" name="name" class="border border-input-border rounded px-3 h-12 w-1/3" />
      <input v-model="phoneNumber" type="text" name="phoneNumber" class="border border-input-border rounded px-3 h-12 w-1/3" />
      <select name="noOfPeople" id="noOfPeople" v-model="noOfPeople" class="border border-input-border rounded px-3 h-12 w-1/3" style="border-width: 1px">
        <option value="" disabled>{{ t("bivalentVaccine.pleaseSelectNoOfPeople") }}</option>

        <template v-for="(item, index) in bivalentVaccineStore.form.noOfPeopleList" :key="'noOfPeople' + index">
          <option :value="item.id" class="noofpeople-item">{{ item.id }}{{ t(item.displayName) }}</option>
        </template>
      </select>
    </div>

    <div class="flex space-x-14 pt-10">
      <span class="w-1/3 flex items-center"
        ><span>{{ t("bivalentVaccine.introducerID") }}</span> <span class="text-red-500 xl:text-xs xl: font-bold pl-1" id="introducerIDError">{{ introducerIDError }}</span>
      </span>
      <span class="w-1/3"></span>
      <span class="w-1/3"></span>
    </div>
    <div class="flex space-x-14">
      <input v-model="introducerID" type="text" name="introducerID" class="border border-input-border rounded px-3 h-12 w-1/3" :placeholder="t('common.pleaseInput')" />
      <span class="px-3 w-1/3"></span>
      <span class="px-3 w-1/3"></span>
    </div>

    <div class="flex space-x-14 pt-10">
      <span class="w-full flex items-center">
        <label class="checkbox-container"
          >{{ t("bivalentVaccine.agreement") }}
          <input type="checkbox" id="agreement" name="agreement" value="agreement" v-model="agreement" />
          <span class="checkbox-checkmark"></span>
        </label>
        <div class="text-red-500 xl:text-xs xl: font-bold pl-1" id="agreementError">{{ agreementError }}</div>
      </span>
    </div>
    <button-util buttonType="submit" bgColor="#603813" label="bivalentVaccine.bookAppointment" class="mt-10" />
  </Form>
</template>

<script setup lang="ts">
import { useHeaderStore } from "@/stores/header";
import { useBivalentVaccineStore } from "@/stores/bivalentVaccine";
import { computed, onMounted, ref, toRefs } from "vue";
import { useForm, useField } from "vee-validate";
import { object, string, boolean } from "yup";
import { useRouter } from "vue-router";
import { useCommonStore } from "@/stores/common";
import moment from "moment";
import { useI18n } from "vue-i18n";

const headerStore = useHeaderStore();
const bivalentVaccineStore = useBivalentVaccineStore();
const commonStore = useCommonStore();
const router = useRouter();
const { t, locale } = useI18n();

onMounted(async () => {});

const phoneRegExp = /^((\+[1-9]{1,4}[ -]?)|(\([0-9]{2,3}\)[ -]?)|([0-9]{2,4})[ -]?)*?[0-9]{3,4}[ -]?[0-9]{3,4}$/;
// Define a validation schema
const schema = computed(() => {
  return object({
    option: string().required(t("bivalentVaccine.optionRequired")),
    name: string().required(t("bivalentVaccine.nameRequired")),
    phoneNumber: string().required(t("bivalentVaccine.phoneNumberRequired")).matches(phoneRegExp, t("bivalentVaccine.phoneNumberInvalid")),
    introducerID: string(),
    noOfPeople: string().required(t("bivalentVaccine.pleaseSelectNoOfPeople")),
    agreement: boolean().oneOf([true], t("bivalentVaccine.agreementRequired")),
  });
});
// Create a form context with validation schema
const { handleSubmit, isSubmitting, meta } = useForm({
  validationSchema: schema,
  initialValues: {
    option: "selfPayingVaccine",
    name: "",
    phoneNumber: "",
    introducerID: "",
    noOfPeople: "",
    agreement: false,
  },
});

const { value: option, errorMessage: optionError } = useField<string>("option");
const { value: name, errorMessage: nameError } = useField<string>("name");
const { value: phoneNumber, errorMessage: phoneNumberError } = useField<string>("phoneNumber");
const { value: introducerID, errorMessage: introducerIDError } = useField<string>("introducerID");
const { value: noOfPeople, errorMessage: noOfPeopleError } = useField<string>("noOfPeople");
const { value: agreement, errorMessage: agreementError } = useField<string>("agreement");

const sendMsg = handleSubmit(async (values, actions) => {
  let detail = {
    contactEntryPoint: headerStore.routerName,
    title: t("bivalentVaccine.bookVaccineAppointment"),
    formType: t("bivalentVaccine.group"),
    option: option.value == "selfPayingVaccine" ? t("bivalentVaccine.selfPayingVaccine") : "",
    name: name.value,
    phoneNumber: phoneNumber.value,
    introducerID: introducerID.value,
    noOfPeople: noOfPeople.value + t("bivalentVaccine.people"),
    agreement: agreement.value,
  };

  console.log("sendMsg", detail);

  detail.message = "類別: " + detail.formType + "<br />" + "項目: " + detail.option + "<br />" + "姓別: " + detail.name + "<br />" + "手機: " + detail.phoneNumber + "<br />" + "人數: " + detail.noOfPeople + "<br />" + "介紹人編號 (如有): " + detail.introducerID + "<br />";

  //replace \n to <br> for new line

  var isSuccess = await bivalentVaccineStore.bookAppointment(detail);
  console.log("isSuccess?" + isSuccess);
  if (isSuccess) actions.resetForm();
});
</script>

<style scoped>
.el-input {
  width: 33.333333% !important;
}
.drop-down-group .group-name {
  color: #c4c4c4;
}
</style>